import { memo, useMemo } from "react";
import { Grid, Typography, useMediaQuery, useTheme } from "@material-ui/core";

import { TileSelectProps } from "./types";
import { TileCardMemo } from "./tile-card";
import { useController } from "react-hook-form";
import { useTileCardStyles } from "./styles";

export const TileSelect = <
  TFieldValue extends string | number = string,
  TFormValues = Record<string, unknown>,
>(
  props: TileSelectProps<TFieldValue, TFormValues>,
) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"), {
    noSsr: true,
  });

  const { control, label, name, options, variant } = props;
  const styles = useTileCardStyles();

  const { field } = useController({
    name,
    control,
  });

  const isCompact = useMemo(() => {
    return variant === "vertical-compact";
  }, [variant]);

  const isVertical = useMemo(() => {
    return variant === "vertical";
  }, [variant]);

  const size = useMemo(() => {
    if (variant === "vertical-compact") {
      return 3;
    }

    return 6;
  }, [variant]);

  const mobileSize = useMemo(() => {
    if (variant === "vertical" || variant === "vertical-compact") {
      return 6;
    }

    return 12;
  }, [variant]);

  return (
    <div>
      {label && (
        <Typography
          variant="subtitle1"
          className={styles.selectLabel}
          component="p"
        >
          {label}
        </Typography>
      )}

      <Grid container spacing={isVertical && !isMobile ? 6 : 2}>
        {options.map((option) => (
          <Grid
            item
            xs={mobileSize}
            sm={isCompact ? 6 : size}
            md={size}
            key={String(option.value)}
          >
            <TileCardMemo<TFieldValue>
              label={option.label}
              icon={option.icon}
              isSelected={field.value === option.value}
              onChange={(value) => field.onChange(value)}
              value={option.value}
              variant={variant}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export const TileSelectMemo = memo(TileSelect) as <
  TFieldValue extends string | number = string,
  TFormValues = Record<string, unknown>,
>(
  props: TileSelectProps<TFieldValue, TFormValues>,
) => JSX.Element;
